import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useParams } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ServerProvider } from './contexts/ServerContext';
import { SettingsProvider } from './contexts/SettingsContext';
import MainLayout from './layouts/MainLayout';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import EmbeddingSyncAlertListener from './components/EmbeddingSyncAlertListener';
import { getBasePath } from './utils/runtime';

const LoginPage = lazy(() => import('./pages/LoginPage'));
const OAuthConsentPage = lazy(() => import('./pages/OAuthConsentPage'));
const DashboardPage = lazy(() => import('./pages/Dashboard'));
const CredentialsPage = lazy(() => import('./pages/CredentialsPage'));
const ServersPage = lazy(() => import('./pages/ServersPage'));
const ConnectorCatalogPage = lazy(() => import('./pages/ConnectorCatalogPage'));
const GroupsPage = lazy(() => import('./pages/GroupsPage'));
const UsersPage = lazy(() => import('./pages/UsersPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const MarketPage = lazy(() => import('./pages/MarketPage'));
const LogsPage = lazy(() => import('./pages/LogsPage'));
const ActivityPage = lazy(() => import('./pages/ActivityPage'));
const PromptsPage = lazy(() => import('./pages/PromptsPage'));
const ResourcesPage = lazy(() => import('./pages/ResourcesPage'));

// Helper component to redirect legacy cloud server routes to the local market page
const CloudRedirect: React.FC = () => {
  const { serverName } = useParams<{ serverName: string }>();
  return <Navigate to={`/market/${serverName}`} replace />;
};

const RouteFallback: React.FC = () => (
  <div className="flex min-h-screen items-center justify-center text-sm text-gray-500">
    Loading...
  </div>
);

function App() {
  const basename = getBasePath();
  return (
    <ThemeProvider>
      <AuthProvider>
        <ServerProvider>
          <ToastProvider>
            <SettingsProvider>
              <Router basename={basename}>
                <EmbeddingSyncAlertListener />
                <Routes>
                  {/* 公共路由 */}
                  <Route
                    path="/login"
                    element={
                      <Suspense fallback={<RouteFallback />}>
                        <LoginPage />
                      </Suspense>
                    }
                  />

                  {/* OAuth consent screen: server injects the consent context
                      into the SPA shell served at /oauth/authorize */}
                  <Route
                    path="/oauth/authorize"
                    element={
                      <Suspense fallback={<RouteFallback />}>
                        <OAuthConsentPage />
                      </Suspense>
                    }
                  />

                  {/* 受保护的路由，使用 MainLayout 作为布局容器 */}
                  <Route element={<ProtectedRoute />}>
                    <Route element={<MainLayout />}>
                      <Route path="/" element={<DashboardPage />} />
                      <Route path="/credentials" element={<CredentialsPage />} />
                      <Route path="/servers" element={<ServersPage />} />
                      <Route path="/groups" element={<GroupsPage />} />
                      <Route path="/prompts" element={<PromptsPage />} />
                      <Route path="/resources" element={<ResourcesPage />} />
                      {/* Leanport: admin-only pages show members an access-restricted panel */}
                      <Route element={<AdminRoute />}>
                        <Route path="/users" element={<UsersPage />} />
                        <Route path="/catalog" element={<ConnectorCatalogPage />} />
                        <Route path="/logs" element={<LogsPage />} />
                        <Route path="/activity" element={<ActivityPage />} />
                      </Route>
                      {/* Leanport: installing from the market is admin-only under MCPHUB_ADMIN_ONLY_CONFIG */}
                      <Route element={<AdminRoute onlyWhenAdminOnlyConfig />}>
                        <Route path="/market" element={<MarketPage />} />
                        <Route path="/market/:serverName" element={<MarketPage />} />
                      </Route>
                      {/* Legacy cloud routes redirect to the local market page */}
                      <Route path="/cloud" element={<Navigate to="/market" replace />} />
                      <Route path="/cloud/:serverName" element={<CloudRedirect />} />
                      <Route path="/settings" element={<SettingsPage />} />
                    </Route>
                  </Route>

                  {/* 未匹配的路由重定向到首页 */}
                  <Route path="*" element={<Navigate to="/" />} />
                </Routes>
              </Router>
            </SettingsProvider>
          </ToastProvider>
        </ServerProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
